import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '../services/api';
import { Spinner, showToast } from '../components/UI';
import { AppShell, TopNav, TopBar, LeftNav, BodyGrid, PageBody } from '../components/Layout';

const AGENT_COLORS = {
  ceo: '#0e0e0d', builder: '#378add', content: '#5dcaa5',
  marketing: '#c8563a', research: '#e8a87c',
};

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(ts).toLocaleDateString();
}

// ── Markdown renderer (lightweight, no deps) ──────────────────────────────────
function MarkdownBlock({ body }) {
  const html = body
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[hul])(.+)$/gm, '<p>$1</p>');
  return (
    <div
      style={md}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ── Single artifact renderer ──────────────────────────────────────────────────
function ArtifactRenderer({ artifact }) {
  const isSite = artifact.type === 'site';
  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <span style={{ fontSize: '18px' }}>{isSite ? '🌐' : artifact.type === 'research' ? '🔍' : '📄'}</span>
        <div>
          <strong style={{ fontSize: '14px', display: 'block' }}>{artifact.title}</strong>
          <span style={{ fontSize: '11px', color: 'var(--ink-mute)' }}>
            {artifact.type} · v{artifact.version} · {timeAgo(artifact.created_at)}
          </span>
        </div>
        {artifact.live_url && (
          <a href={artifact.live_url} target="_blank" rel="noreferrer"
            style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--accent)', textDecoration: 'none' }}>
            Open live site ↗
          </a>
        )}
      </div>

      {isSite ? (
        <iframe
          srcDoc={artifact.body}
          title={artifact.title}
          style={{
            width: '100%', height: '600px', border: '1px solid var(--paper-3)',
            borderRadius: 'var(--r)', background: '#fff',
          }}
          sandbox="allow-scripts allow-same-origin"
        />
      ) : (
        <div style={{
          border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
          padding: '24px 28px', background: 'var(--white)',
        }}>
          <MarkdownBlock body={artifact.body || ''} />
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function TaskOutputPage() {
  const { workspaceId, taskId } = useParams();
  const navigate = useNavigate();

  const [task, setTask]           = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [running, setRunning]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  // Load task detail on mount
  useEffect(() => {
    loadTask();
  }, [taskId, workspaceId]);

  async function loadTask() {
    try {
      const data = await apiFetch(`/api/workspaces/${workspaceId}/tasks/${taskId}`);
      setTask(data);
      setArtifacts(data.artifacts || []);
    } catch (e) {
      setError(e.message || 'Failed to load task.');
    } finally {
      setLoading(false);
    }
  }



  if (loading) return (
    <AppShell>
      <TopNav breadcrumb="Output" />
      <TopBar />
      <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={32} />
      </div>
    </AppShell>
  );

  if (error || !task) return (
    <AppShell>
      <TopNav breadcrumb="Output" />
      <TopBar />
      <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
        <p style={{ fontSize: '14px', color: 'var(--ink-mute)' }}>{error || 'Task not found.'}</p>
        <button onClick={() => navigate(-1)} style={s.cancelBtn}>← Go back</button>
      </div>
    </AppShell>
  );

  const agentColor = AGENT_COLORS[task.agent_type] || '#888';
  const hasArtifacts = artifacts.length > 0;

  return (
    <AppShell>
      <TopNav breadcrumb={task.title} />
      <TopBar />
      <BodyGrid>
        <LeftNav />
        <PageBody style={{ maxWidth: 'none' }}>
          <div style={{ maxWidth: '900px' }}>

            {/* ── Header ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '24px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <Link
                    to={`/dashboard/tasks/${taskId}?workspace_id=${workspaceId}`}
                    style={{ fontSize: '12px', color: 'var(--ink-mute)', textDecoration: 'none' }}>
                    ← Back to task
                  </Link>
                </div>
                <h1 style={{ fontFamily: 'var(--serif)', fontSize: '26px', fontWeight: '400', lineHeight: 1.25, marginBottom: '8px' }}>
                  {task.title}
                </h1>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px',
                  fontWeight: '500', padding: '3px 10px', borderRadius: '10px',
                  background: `${agentColor}18`, color: agentColor,
                }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: agentColor }} />
                  {task.agent_type}
                </span>
              </div>
            </div>

            {/* ── Running state ── */}
            {running && (
              <div style={{
                background: 'rgba(55,138,221,0.06)', border: '1px solid rgba(55,138,221,0.2)',
                borderRadius: 'var(--r)', padding: '16px 20px', marginBottom: '20px',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}>
                <Spinner size={18} />
                <div>
                  <strong style={{ fontSize: '13px', color: '#378add', display: 'block' }}>
                    Agent is running…
                  </strong>
                  <span style={{ fontSize: '12px', color: '#378add', opacity: 0.8 }}>
                    This may take up to a minute. You can navigate away and come back.
                  </span>
                </div>
              </div>
            )}

            {/* ── Artifacts ── */}
            {hasArtifacts ? (
              <section style={s.section}>
                <h2 style={s.sTitle}>Output</h2>
                {artifacts.map(a => (
                  <ArtifactRenderer key={a.id} artifact={a} />
                ))}
              </section>
            ) : !running && (
              <section style={{ ...s.section, textAlign: 'center', padding: '48px 24px' }}>
                <p style={{ fontSize: '32px', marginBottom: '12px' }}></p>
                <p style={{ fontSize: '14px', color: 'var(--ink-mute)', marginBottom: '20px' }}>
                  This task hasn't been run yet.
                </p>
              </section>
            )}

            {/* ── Task description ── */}
            {task.description && (
              <section style={s.section}>
                <h2 style={s.sTitle}>Task description</h2>
                <p style={{ fontSize: '14px', color: 'var(--ink-soft)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {task.description}
                </p>
              </section>
            )}

          </div>
        </PageBody>
      </BodyGrid>
    </AppShell>
  );
}

const s = {
  section: {
    background: 'var(--white)', border: '1px solid var(--paper-3)',
    borderRadius: 'var(--r)', padding: '20px 24px', marginBottom: '16px',
  },
  sTitle: {
    fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: '600',
    color: 'var(--ink)', marginBottom: '16px',
  },
  runBtn: {
    padding: '10px 20px', background: 'var(--ink)', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '13px',
    fontWeight: '500', cursor: 'pointer', fontFamily: 'var(--sans)',
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  cancelBtn: {
    padding: '8px 14px', background: 'none', color: 'var(--ink-mute)',
    border: '1px solid var(--paper-3)', borderRadius: '6px',
    fontSize: '13px', cursor: 'pointer', fontFamily: 'var(--sans)',
  },
};

const md = {
  fontSize: '14px', lineHeight: 1.8, color: 'var(--ink)',
  fontFamily: 'var(--sans)',
};